import { useState, useRef } from 'react';
import api from '../lib/api';

// Editor NLE project file on a Live Production video card (latest-only).
// presign → direct PUT to S3 → confirm; download via a short-lived signed URL.
// Project files only (no media) — a generous cap lives on the server (250 MB).
const ACCEPT = '.prproj,.drp,.aep,.fcpxml,.fcpxmld,.xml,.aaf,.zip';

function fmtBytes(b) {
  if (b == null) return '';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3)   return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}
function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}

export default function ProjectFileField({ record, canEdit, members, onChange }) {
  const [phase,    setPhase]    = useState('idle'); // idle | uploading | confirming | downloading | error
  const [progress, setProgress] = useState(0);
  const [error,    setError]    = useState('');
  const inputRef = useRef(null);

  const hasFile     = !!record.projectFileName;
  const uploaderName = members?.find(m => m.id === record.projectFileUploadedBy)?.name || null;
  const busy = phase === 'uploading' || phase === 'confirming';

  async function handleFile(file) {
    if (!file) return;
    setError(''); setPhase('uploading'); setProgress(0);
    try {
      const presign = await api.post(`/api/live-production/${record.id}/project-file/presign`, {
        fileName: file.name, fileSize: file.size, contentType: file.type || 'application/octet-stream',
      });
      const { uploadUrl, s3Key } = presign.data;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = e => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });

      setPhase('confirming');
      const confirm = await api.post(`/api/live-production/${record.id}/project-file/confirm`, {
        s3Key, fileName: file.name, fileSize: file.size,
      });
      onChange(confirm.data); // merge new project-file fields into the parent record
      setPhase('idle'); setProgress(0);
    } catch (err) {
      setPhase('error');
      setError(err.response?.data?.error || err.message || 'Upload failed.');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleDownload() {
    setError(''); setPhase('downloading');
    try {
      const res = await api.get(`/api/live-production/${record.id}/project-file/download`);
      window.open(res.data.url, '_blank');
    } catch (err) {
      setError(err.response?.data?.error || 'Download failed.');
    } finally { setPhase('idle'); }
  }

  async function handleRemove() {
    if (!window.confirm('Remove the project file? This deletes the uploaded file.')) return;
    setError(''); setPhase('confirming');
    try {
      const res = await api.delete(`/api/live-production/${record.id}/project-file`);
      onChange(res.data);
      setPhase('idle');
    } catch (err) {
      setPhase('error');
      setError(err.response?.data?.error || 'Remove failed.');
    }
  }

  return (
    <div className="vc-field full">
      <label>Project File</label>
      <input ref={inputRef} type="file" accept={ACCEPT} style={{ display: 'none' }}
             onChange={e => handleFile(e.target.files?.[0])} />

      {hasFile ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border-mid)' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)', wordBreak: 'break-all' }}>{record.projectFileName}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 2 }}>
              {fmtBytes(record.projectFileSize)}
              {uploaderName ? ` · ${uploaderName}` : ''}
              {record.projectFileUploadedAt ? ` · ${fmtDate(record.projectFileUploadedAt)}` : ''}
            </div>
          </div>
          <button className="btn-primary" style={{ fontSize: 12 }} onClick={handleDownload} disabled={phase === 'downloading'}>
            {phase === 'downloading' ? 'Opening…' : '↓ Download'}
          </button>
          {canEdit && (
            <>
              <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => inputRef.current?.click()} disabled={busy}>Replace</button>
              <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--s-incomplete-t)' }} onClick={handleRemove} disabled={busy}>Remove</button>
            </>
          )}
        </div>
      ) : canEdit ? (
        <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => inputRef.current?.click()} disabled={busy}>
          ⬆ Upload project file
        </button>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>No project file uploaded.</div>
      )}

      {busy && (
        <div style={{ marginTop: 8 }}>
          <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-card2)', overflow: 'hidden', marginBottom: 5 }}>
            <div style={{ height: '100%', width: `${phase === 'confirming' ? 100 : progress}%`, background: 'var(--violet)', transition: 'width 0.2s', borderRadius: 2 }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{phase === 'confirming' ? 'Saving…' : `Uploading ${progress}%`}</div>
        </div>
      )}

      {error && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--s-incomplete-t)' }}>{error}</div>}
    </div>
  );
}
